export const TROUBLESHOOTING = [
  {
    label: "429 Too Many Requests",
    chip: true,
    cause: "Request rate exceeded the 5 req/s limit or 10-request burst allowance.",
    fix: "Slow down and retry with exponential backoff. Batch tool calls where possible to stay within limits.",
  },
  {
    label: "401 Unauthorized",
    chip: true,
    cause:
      "Access token is missing, expired, malformed, or not accepted by the gateway.",
    fix: [
      { t: "text", v: "Exchange your refresh token for a new access token and confirm the " },
      { t: "chip", v: "Authorization: Bearer" },
      { t: "text", v: " header is sent on every MCP request." },
    ],
  },
  {
    label: "No tools appear",
    chip: false,
    cause:
      "The client may not have connected successfully or may be using the wrong transport.",
    fix: [
      { t: "text", v: "Run " },
      { t: "chip", v: "tools/list" },
      { t: "text", v: " with curl, then confirm whether the client expects remote HTTP, Streamable HTTP, SSE, or a local proxy." },
    ],
  },
  {
    label: "Invalid route result",
    chip: false,
    cause:
      "The origin or destination may be reversed, missing, or using the wrong coordinate system.",
    fix: [
      { t: "text", v: "Use " },
      { t: "chip", v: "address___search" },
      { t: "text", v: " first to resolve place names, then pass WGS84 coordinates as " },
      { t: "chip", v: "lat,lng" },
      { t: "text", v: "." },
    ],
  },
  {
    label: "400 Bad Request",
    chip: true,
    cause: "The JSON-RPC envelope or tool arguments are not valid.",
    fix: [
      { t: "text", v: "Check that requests include " },
      { t: "chip", v: "jsonrpc" },
      { t: "text", v: ", " },
      { t: "chip", v: "method" },
      { t: "text", v: ", " },
      { t: "chip", v: "params" },
      { t: "text", v: ", and " },
      { t: "chip", v: "id" },
      { t: "text", v: ", and that enum values match the tool reference." },
    ],
  },
  {
    label: "Slow or large response",
    chip: false,
    cause:
      "The query may be broad, especially for thematic datasets or address pagination.",
    fix: "Use bounding boxes, smaller radii, specific search strings, and pagination where available.",
  },
];

export const QUICK_REFERENCE = [
  {
    name: "address___search",
    category: "Search",
    categoryKey: "search",
    required: [
      { t: "chip", v: "searchVal" },
      { t: "text", v: "," },
      { t: "chip", v: "returnGeom" },
      { t: "text", v: "," },
      { t: "chip", v: "getAddrDetails" },
    ],
    use: "Free-text address, building, road, bus stop number, or postal code search",
  },
  {
    name: "reversegeocode___address_from_latlng",
    category: "Geocoding",
    categoryKey: "geocoding",
    required: [
      { t: "chip", v: "location" },
      { t: "text", v: "as" },
      { t: "chip", v: "lat,lng" },
    ],
    use: "Finding the nearest address for a WGS84 coordinate",
  },
  {
    name: "reversegeocode___address_from_xy",
    category: "Geocoding",
    categoryKey: "geocoding",
    required: [
      { t: "chip", v: "location" },
      { t: "text", v: "as" },
      { t: "chip", v: "x,y" },
    ],
    use: "Finding an address from SVY21 coordinates",
  },
  {
    name: "routing___transport",
    category: "Routing",
    categoryKey: "routing",
    required: [
      { t: "chip", v: "routeType" },
      { t: "text", v: "," },
      { t: "chip", v: "start" },
      { t: "text", v: "," },
      { t: "chip", v: "end" },
    ],
    use: "Planning public transport, driving, walking, or cycling routes",
  },
  {
    name: "nearby___bus_stops",
    category: "Nearby",
    categoryKey: "nearby",
    required: [
      { t: "chip", v: "latitude" },
      { t: "text", v: "," },
      { t: "chip", v: "longitude" },
    ],
    use: "Finding nearby bus stops within a radius",
  },
  {
    name: "nearby___mrt_stations",
    category: "Nearby",
    categoryKey: "nearby",
    required: [
      { t: "chip", v: "latitude" },
      { t: "text", v: "," },
      { t: "chip", v: "longitude" },
    ],
    use: "Finding nearby MRT and LRT stations",
  },
  {
    name: "thematic___allThemes",
    category: "Thematic",
    categoryKey: "thematic",
    required: [],
    use: "Discovering available thematic dataset identifiers",
  },
  {
    name: "thematic___themeLayer",
    category: "Thematic",
    categoryKey: "thematic",
    required: [{ t: "chip", v: "queryName" }],
    use: "Retrieving records for a selected thematic map layer",
  },
  {
    name: "coordinates___convert4326To3414",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "latitude" },
      { t: "text", v: "," },
      { t: "chip", v: "longitude" },
    ],
    use: "Converting WGS84 lat/lng to SVY21 X/Y",
  },
  {
    name: "coordinates___convert4326To3857",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "latitude" },
      { t: "text", v: "," },
      { t: "chip", v: "longitude" },
    ],
    use: "Converting WGS84 lat/lng to EPSG:3857 X/Y",
  },
  {
    name: "coordinates___convert3414To4326",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "X" },
      { t: "text", v: "," },
      { t: "chip", v: "Y" },
    ],
    use: "Converting SVY21 X/Y to WGS84 lat/lng",
  },
  {
    name: "coordinates___convert3414To3857",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "X" },
      { t: "text", v: "," },
      { t: "chip", v: "Y" },
    ],
    use: "Converting SVY21 X/Y to EPSG:3857 X/Y",
  },
  {
    name: "coordinates___convert3857To4326",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "X" },
      { t: "text", v: "," },
      { t: "chip", v: "Y" },
    ],
    use: "Converting EPSG:3857 X/Y to WGS84 lat/lng",
  },
  {
    name: "coordinates___convert3857To3414",
    category: "Coordinates",
    categoryKey: "coordinates",
    required: [
      { t: "chip", v: "X" },
      { t: "text", v: "," },
      { t: "chip", v: "Y" },
    ],
    use: "Converting EPSG:3857 X/Y to SVY21 X/Y",
  },
  {
    name: "popquery___getPlanningareaNames",
    category: "Population",
    categoryKey: "population",
    required: [],
    use: "Listing valid planning-area names for the census tools",
  },
  {
    name: "popquery___getAllPlanningarea",
    category: "Population",
    categoryKey: "population",
    required: [],
    use: "Getting every planning area with its boundary geometry",
  },
  {
    name: "popquery___getPlanningarea",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "latitude" },
      { t: "text", v: "," },
      { t: "chip", v: "longitude" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Finding which planning area a coordinate falls in",
  },
  {
    name: "popquery___getPopulationAgeGroup",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: [
      { t: "text", v: "Age profile of an area; optional " },
      { t: "chip", v: "gender" },
      { t: "text", v: " filter" },
    ],
  },
  {
    name: "popquery___getEthnicGroup",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: [
      { t: "text", v: "Ethnic mix of an area; optional " },
      { t: "chip", v: "gender" },
      { t: "text", v: " filter" },
    ],
  },
  {
    name: "popquery___getMaritalStatus",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: [
      { t: "text", v: "Marital status of residents; optional " },
      { t: "chip", v: "gender" },
      { t: "text", v: " filter" },
    ],
  },
  {
    name: "popquery___getReligion",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Religious profile of an area",
  },
  {
    name: "popquery___getLanguageLiterate",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Language literacy of residents",
  },
  {
    name: "popquery___getSpokenAtHome",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Languages spoken at home",
  },
  {
    name: "popquery___getHouseholdSize",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Household size distribution",
  },
  {
    name: "popquery___getHouseholdStructure",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Household structure",
  },
  {
    name: "popquery___getTenancy",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Household tenancy",
  },
  {
    name: "popquery___getTypeOfDwellingHousehold",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Household counts by dwelling type",
  },
  {
    name: "popquery___getTypeOfDwellingPop",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Population counts by dwelling type",
  },
  {
    name: "popquery___getHouseholdMonthlyIncomeWork",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Household monthly income from work",
  },
  {
    name: "popquery___getEconomicStatus",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: [
      { t: "text", v: "Economic status of residents; optional " },
      { t: "chip", v: "gender" },
      { t: "text", v: " filter" },
    ],
  },
  {
    name: "popquery___getIncomeFromWork",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Income from work",
  },
  {
    name: "popquery___getIndustry",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Industry of employment",
  },
  {
    name: "popquery___getOccupation",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Occupation of residents",
  },
  {
    name: "popquery___getEducationAttending",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "Education attendance",
  },
  {
    name: "popquery___getModeOfTransportWork",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "How residents travel to work",
  },
  {
    name: "popquery___getModeOfTransportSchool",
    category: "Population",
    categoryKey: "population",
    required: [
      { t: "chip", v: "planningArea" },
      { t: "text", v: "," },
      { t: "chip", v: "year" },
    ],
    use: "How residents travel to school",
  },
];